import { Box, createStyles, LoadingOverlay } from '@mantine/core';
import { isEqual } from 'lodash/fp';
import React, { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import {
  PortalConfigType,
  useConfig,
  usePortalConfig,
  useUpdatePortalConfig,
} from '@portals/api/partners';
import { defaultStoreBannerSrc } from '@portals/framework';
import { TenantType } from '@portals/types';

import { BrandingForm } from './branding-forms/BrandingForm';
import { BrandingPreview } from './branding-previews/BrandingPreview';
import { BrandingContext } from './branding.context';
import { BrandingType } from './branding.types';
import { Footer } from './Footer';

export function Branding() {
  const { classes, theme } = useStyles();
  const config = useConfig();
  const portalConfig = usePortalConfig();
  const updatePortalConfig = useUpdatePortalConfig();
  const params = useParams<{ type: BrandingType }>();

  const [localPortalConfig, setLocalPortalConfig] = useState(portalConfig.data);

  const isPristine = useMemo(() => {
    if (updatePortalConfig.isLoading) return true;

    switch (params.type) {
      case BrandingType.CustomerPortal:
        return isEqual(localPortalConfig?.design, portalConfig?.data?.design);

      case BrandingType.Email:
        return isEqual(
          localPortalConfig.email_settings,
          portalConfig?.data?.email_settings
        );

      case BrandingType.Store:
        return isEqual(
          localPortalConfig?.design?.store,
          portalConfig?.data?.design?.store
        );

      default:
        return true;
    }
  }, [
    updatePortalConfig.isLoading,
    params.type,
    localPortalConfig,
    portalConfig?.data,
  ]);

  const updateLocalPortalConfig = (updatedConfig: PortalConfigType) => {
    if (updatePortalConfig.isLoading) return;

    setLocalPortalConfig(updatedConfig);
  };

  const revertChanges = () => {
    if (updatePortalConfig.isLoading) return;

    setLocalPortalConfig(portalConfig?.data);
  };

  const saveChanges = async () => {
    if (updatePortalConfig.isLoading) return;

    try {
      const updatedPortalConfig = await updatePortalConfig.mutateAsync({
        updatedConfig: localPortalConfig,
      });

      setLocalPortalConfig(updatedPortalConfig);
    } catch (error) {
      console.error(error);
    }
  };

  const defaultValues = useMemo<
    Pick<PortalConfigType, 'design' | 'email_settings'>
  >(
    () => ({
      design: {
        sign_in_page: {
          logo: '',
          login_image: '',
          tagline: null,
          tagline_color: theme.white,
          powered_by_xyte_label: true,
          background_mode: 'color',
          background_color: theme.colors.blue_accent[4],
          background_image: '',
          primary_color: theme.colors.blue_accent[4],
          text_color: theme.white,
        },
        navigation_menu: {
          logo: '',
          menu_background_color: theme.white,
          company_text_color: theme.colors.blue_gray[8],
          menu_text_color: theme.colors.blue_gray[6],
          selected_section_background_color: theme.colors.blue_accent[4],
          selected_section_text_color: theme.white,
          hovered_section_background_color: theme.colors.gray[1],
          hovered_section_text_color: theme.colors.blue_gray[6],
        },
        store: {
          logo:
            config?.data?.[TenantType.Partner]?.logo_url ||
            portalConfig?.data?.design?.navigation_menu?.logo ||
            portalConfig?.data?.design?.sign_in_page?.logo ||
            'logo_full_dark.svg',
          tagline: '',
          banner: defaultStoreBannerSrc,
          with_banner: false,

          menu_buttons_color: theme.colors.blue_accent[0],
          menu_items_text_color: theme.colors.blue_accent[4],
          buttons_color: theme.colors.blue_accent[4],
          buttons_text_color: theme.white,
        },
      },
      email_settings: {
        primary: '#FEC516',
        secondary: '#444444',
        tertiary: '#3CADE1',
        color: '#FEC516',

        site_url: '',
        logo: 'logo_yellow.png',
        markdown: 'Thank you,\\\n **The XYTE team**',
      },
    }),
    [
      theme.white,
      theme.colors.blue_accent,
      theme.colors.blue_gray,
      theme.colors.gray,
      config?.data,
      portalConfig?.data?.design?.navigation_menu?.logo,
      portalConfig?.data?.design?.sign_in_page?.logo,
    ]
  );

  return (
    <Box className={classes.container}>
      {portalConfig.isFetched ? (
        <BrandingContext.Provider
          value={{
            portalConfig: localPortalConfig,
            updatePortalConfig: updateLocalPortalConfig,
            isLoading: updatePortalConfig.isLoading,
            isPristine,
            revertChanges,
            saveChanges,
            defaultValues,
          }}
        >
          <div className={classes.content}>
            <BrandingForm />

            <BrandingPreview />
          </div>

          <Footer />
        </BrandingContext.Provider>
      ) : null}

      <LoadingOverlay visible={updatePortalConfig.isLoading} />
    </Box>
  );
}

const useStyles = createStyles(() => ({
  container: {
    display: 'grid',
    gridTemplateRows: '1fr 56px',
    height: '100%',
    position: 'relative',
  },
  content: {
    width: '100%',
    display: 'grid',
    gridTemplateColumns: '315px 1fr',
  },
}));
